import { FunctionComponent, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import LoadingWidget from "../../../context_providers/modal/loader";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import { WarehouseModel } from "../models/warehouse_model";
import { ItemModelV2 } from "../models/item_model";
import BulkStockUploadForm, {
  StockRequestEditInitial,
} from "./bulk_stock_upload/BulkStockUploadForm";
import {
  BulkTypeByAction,
  BulkUploadType,
  GetStockActionRequestApi,
  StockActionRequest,
} from "./bulk_stock_upload/bulk_stock_upload_repo";

const StockActionRequestEditScreen: FunctionComponent = () => {
  const { id } = useParams<{ id: string }>();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState<StockActionRequest | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await GetStockActionRequestApi(id);
        if (res?.success && res.data) setRequest(res.data);
        else
          showToast({
            type: ToastType.error,
            text: res?.message || res?.error || "Failed to load request",
          });
      } catch (e: any) {
        showToast({ type: ToastType.error, text: e.message });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) return <LoadingWidget />;
  if (!request)
    return (
      <div id="request_not_found" className="p-6 text-gray-500">
        Request not found.
      </div>
    );

  const type = BulkTypeByAction[request.action] as BulkUploadType;
  const initial: StockRequestEditInitial = {
    warehouse: request.warehouse_id
      ? ({
          id: request.warehouse_id,
          name: request.warehouse_name,
        } as unknown as WarehouseModel)
      : null,
    item: request.item_code
      ? ({
          item_code: request.item_code,
          name: request.item_code,
          description: "",
        } as unknown as ItemModelV2)
      : null,
    toItem: request.to_item_code
      ? ({
          item_code: request.to_item_code,
          name: request.to_item_code,
          description: "",
        } as unknown as ItemModelV2)
      : null,
    remarks: request.remarks ?? "",
    fileName: request.file_name ?? "",
    fileUrl: request.file_url ?? "",
    deviceIds: request.device_ids ?? [],
    rows: (request.rm_rows ?? []).map((r) => ({
      item_code: r.item_code,
      quantity: r.qty,
    })),
    sourceItemCode: request.source_item_code ?? "",
    transformId: request.transform_id ?? "",
  };

  return (
    <div
      id="page_container"
      className="wh-mobile-shrink min-h-screen bg-gray-50 w-full mx-auto"
    >
      <div className="bg-white flex flex-col p-2 sm:p-6 lg:p-4">
        <div className="border-b px-2 pb-2">
          <h1 className="text-xl font-semibold">Edit Stock Request</h1>
          <p className="text-xs text-gray-500">
            {request.action} — {request.status}. Re-upload the CSV only if it
            needs changes; otherwise the original file is reused.
          </p>
        </div>
        <BulkStockUploadForm
          key={type}
          type={type}
          editRequestId={request.id}
          initial={initial}
          fileLabel="Re-upload File (optional)"
        />
      </div>
    </div>
  );
};

export default StockActionRequestEditScreen;
